import React, { useContext, useState } from 'react';
import IconButtonWithTooltip from '../../../common/IconButtonWithTooltip';
import IconButtonWhiteBg from '../../../common/IconButtonWhiteBg';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { AppContext } from '../../../../App';
import Delete from '@mui/icons-material/Delete';
import { IDoc } from '../../../../types/my-types';
import { useNavigate } from 'react-router';
import { useTrainingSetUpdate } from '../../../hooks/useTrainingSetUpdate';

interface IIconDeleteDocumentProps {
  documentId?: string;
  trainingSetId?: string;
  documents?: IDoc[];
  iconType?: 'icon' | 'iconWhiteBg';
}

const IconDeleteDocument: React.FC<IIconDeleteDocumentProps> = ({
  documentId,
  trainingSetId,
  documents,
  iconType = 'icon',
}) => {
  const { setToast, dataPoolId } = useContext(AppContext);

  const [open, setOpen] = useState(false);

  const navigate = useNavigate();

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleCloseConfirmation = () => {
    setOpen(false);
  };

  if (!documents || !documentId || !trainingSetId) {
    return null;
  }

  const { mutateAsync: updateTrainingSet, isPending: isLoadingUpdateTrainingSet } =
    useTrainingSetUpdate({
      onSuccess: () => {
        const newDocument = documents.find((doc) => doc.id !== documentId);
        if (newDocument) {
          navigate(`/data-pools/${dataPoolId}/train/${trainingSetId}/mark/${newDocument.id}`, {
            replace: true,
          });
          navigate(0);
        } else {
          // If no other documents, redirect to the training set details page
          navigate(`/data-pools/${dataPoolId}/train/${trainingSetId}/details`);
        }

        setToast({
          variant: 'success',
          open: true,
          message: 'Deleted',
        });

        handleCloseConfirmation();
      },
      onError: () => {
        setToast({
          variant: 'error',
          open: true,
          message: 'Sorry, failed to delete document.',
        });
      },
    });

  return (
    <>
      {iconType === 'iconWhiteBg' ? (
        <IconButtonWhiteBg
          disabled={isLoadingUpdateTrainingSet}
          onClick={handleClickOpen}
          id="icon-delete-document"
          cypressId="btn-header-delete-document"
          tooltip="Delete Document"
        >
          <Delete style={{ fontSize: '14px', color: 'black' }} />
        </IconButtonWhiteBg>
      ) : (
        <IconButtonWithTooltip
          cypressId="btn-header-delete-document"
          icon={<Delete style={{ color: '#187787' }} />}
          onClick={() => {
            if (isLoadingUpdateTrainingSet) return;
            handleClickOpen();
          }}
          title={`Delete Document`}
          disabled={isLoadingUpdateTrainingSet}
        />
      )}
      <Dialog
        open={open}
        onClose={handleCloseConfirmation}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">{'Confirmation'}</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to delete this document?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmation}>Cancel</Button>
          <Button
            onClick={() => {
              let updatedDocs = documents.filter((doc: IDoc) => doc.id !== documentId);

              updateTrainingSet({
                trainingSetId: trainingSetId,
                data: { documents: updatedDocs.map((doc) => doc.id) },
              });
            }}
            autoFocus
            data-cy="btn-document-delete-modal-yes"
          >
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default IconDeleteDocument;
